import React, { useState } from 'react';
import { Button, CondensedHeader, CondensedLarge, ToastUtil } from '@axiom/ui';

import type { MatchGridSavedColumns } from '../../../schema/spark/match-grid-column-config-api-schema';
import { ChatApi } from '../../api/chat-api';
import type { MatchGridColumnConfigItem } from '../../types/match-grid.types';
import { MatchGridColumnConfigUtil } from '../../utils/match-grid-column-config-util';

import { DEFAULT_MATCH_GRID_COLUMNS } from './match-grid-columns.const';

const SAVE_FAILED_TOAST = 'MATCH_GRID_COLUMNS_SAVE_FAILED';

// One reference, so a reader who has saved nothing does not re-run the conversion every render.
const NOTHING_SAVED: MatchGridSavedColumns = {
  disabledColumns: [],
  enabledColumns: [],
};

/*
  Longer than the ten seconds a toast gets by default, because this one is asking for something
  back rather than only saying what happened.
*/
const SAVE_FAILED_SECONDS = 20;

export type UseMatchGridColumnsProps = {
  /** True once a configuration exists to update. A reader who has none 404s on the read. */
  hasSavedColumns: boolean;
  /** Opens the settings modal, so the toast can offer the reader the way back to it. */
  openSettings: () => void;
  savedColumns: MatchGridSavedColumns | null;
};

/**
 * The grid's column configuration and the saving of it.
 *
 * **A failed save keeps the change on screen.** Rearranging the grid back under the reader's hands
 * reads as a second unasked-for change, so the columns stay as they left them and the toast says
 * the server does not have them - reopening the settings shows the change still there, so saving
 * again from there is the whole retry.
 */
export const useMatchGridColumns = ({
  hasSavedColumns,
  openSettings,
  savedColumns,
}: UseMatchGridColumnsProps) => {
  const [columns, setColumns] = useState(() =>
    MatchGridColumnConfigUtil.toFormPayload({
      columns: DEFAULT_MATCH_GRID_COLUMNS,
      saved: savedColumns ?? NOTHING_SAVED,
    })
  );

  const [hasSaved, setHasSaved] = useState(hasSavedColumns);
  const [isSaving, setIsSaving] = useState(false);

  const saveColumns = async (nextColumns: MatchGridColumnConfigItem[]) => {
    setColumns(nextColumns);
    setIsSaving(true);

    const config = MatchGridColumnConfigUtil.toApiPayload({
      columns: nextColumns,
    });

    try {
      // POST mints the reader's row and PUT replaces it; the server refuses the wrong one.
      if (hasSaved) {
        await ChatApi.updateColumnConfig(config);
      } else {
        await ChatApi.createColumnConfig(config);
        setHasSaved(true);
      }
    } catch {
      ToastUtil.add({
        dismissible: SAVE_FAILED_SECONDS,
        name: SAVE_FAILED_TOAST,
        type: 'error',
        children: (
          <>
            <CondensedHeader>Columns not saved</CondensedHeader>
            <CondensedLarge>
              Use the{' '}
              <Button
                name="MATCH_GRID_COLUMNS_SAVE_FAILED_SETTINGS"
                onClick={() => {
                  openSettings();
                }}
                pattern="secondary"
                variation="minimal"
              >
                settings
              </Button>{' '}
              to try again.
            </CondensedLarge>
          </>
        ),
      });
    } finally {
      setIsSaving(false);
    }
  };

  /*
    A drag reports every column on screen and none of the hidden ones, so the shown half is rebuilt
    from what it reports while the hidden half is carried over as it stands. Carried rather than
    left out: a column named in neither list reads as one added since the save, and the next read
    would bring every hidden column back shown.
  */
  const reorderColumns = async (columnNames: string[]) =>
    saveColumns(
      MatchGridColumnConfigUtil.toFormPayload({
        columns: DEFAULT_MATCH_GRID_COLUMNS,
        saved: {
          disabledColumns: MatchGridColumnConfigUtil.toApiPayload({ columns })
            .disabledColumns,
          enabledColumns: columnNames,
        },
      })
    );

  return { columns, isSaving, reorderColumns, saveColumns };
};
